import React, { useState, useEffect } from 'react';
import { Upload, Modal, message, Spin, Button } from 'antd';
import type { UploadProps, UploadFile, RcFile } from 'antd/es/upload';
import { upload as uploadApi } from '@gatherstar/utils';
import './style.less';

/** ----------------- 文件列表展示组件 ----------------- */
interface FileListProps {
  value?: string | string[]; // 单文件或多文件
  onChange?: (urls: string[] | undefined) => void; // 单文件
  listType?: UploadProps['listType'];
  disabled?: boolean;
  maxCount?: number;
  placeholder?: string;
}

export const FileList: React.FC<FileListProps> = ({
  value,
  onChange,
  listType = 'picture-card',
  disabled = false,
  maxCount = 1,
  placeholder = '点击右侧按钮上传文件',
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    console.log('value:', value);
    const urls = Array.isArray(value) ? value : value ? [value] : [];
    if (urls.length > maxCount) {
      message.error(`最多只能上传 ${maxCount} 个文件`);
      return;
    }
    const list = urls.slice(-maxCount).map((url, index) => ({
      uid: `-${index}`,
      name: url.split('/').pop() || '',
      status: 'done' as const,
      url,
      thumbUrl: url,
    }));
    setFileList(list);
  }, [value]);

  const handleRemove = (file: UploadFile) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    let urls = newFileList.map(item => item.url);
    onChange?.(urls);
  };

  const isImageFile = (file: UploadFile) => {
    const url = file.url || file.thumbUrl || '';
    const fileName = file.name || url;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    return imageExtensions.includes(ext);
  };

  const handlePreview = (file: UploadFile) => {
    if (!isImageFile(file)) {
      message.warning('仅支持预览图片类型文件');
      return;
    }
    setPreviewImage(file.url || file.thumbUrl || '');
    setPreviewOpen(true);
  };

  return (
      <div className="custom-image-upload">
        {fileList.length > 0 ? (
            <>
              <Upload
                  fileList={fileList}
                  listType={listType}
                  onRemove={handleRemove}
                  onPreview={handlePreview}
                  maxCount={maxCount}
                  disabled={disabled}
              />
              <Modal open={previewOpen} footer={null} title="查看图片" onCancel={() => setPreviewOpen(false)}>
                <img alt="preview" style={{ width: '100%' }} src={previewImage}/>
              </Modal>
            </>
        ) : (
            <div className="empty-file-list">{placeholder}</div>
        )}
      </div>
  );
};

/** ----------------- 上传触发组件 ----------------- */
interface FileUploadTriggerProps {
  onUploadSuccess?: (url: string) => void;
  maxSizeMB?: number;
  maxCount?: number;
  accept?: string;
  trigger?: React.ReactNode;
  disabled?: boolean;
}

export const FileUploadTrigger: React.FC<FileUploadTriggerProps> = ({
  onUploadSuccess,
  maxSizeMB = 10,
  maxCount = 1,
  accept = '.jpg,.jpeg,.png',
  trigger,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);

  const beforeUpload = (file: RcFile) => {
    const isLtLimit = file.size / 1024 / 1024 < maxSizeMB;
    if (!isLtLimit) {
      message.error(`文件必须小于 ${maxSizeMB}MB!`);
      return Upload.LIST_IGNORE;
    }
    const fileExt = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const isAccept = accept === '*' || accept.toLowerCase().includes(fileExt);
    if (!isAccept) {
      message.error(`只支持 ${accept} 格式!`);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    try {
      const ext = (file as File).name?.split('.').pop() || 'png';
      const url = await uploadApi({
        file: file,
        fileName: `file-${Date.now()}.${ext}`,
      });
      onSuccess(url);
      onUploadSuccess?.(url);
      message.success('上传成功');
    } catch (err: any) {
      message.error('上传失败');
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const renderTrigger = () => {
    if (trigger) return trigger;
    return (
        <Button className="mr-[-24px]" disabled={disabled || uploading} color="primary" variant="link">
          {uploading ? '上传中...' : '上传文件'}
        </Button>
    );
  };

  return (
      <Spin spinning={uploading}>
        <Upload
            className="custom-image-upload"
            accept={accept}
            beforeUpload={beforeUpload}
            customRequest={customRequest}
            showUploadList={false}
            disabled={disabled || uploading}
        >
          {renderTrigger()}
        </Upload>
      </Spin>
  );
};
